"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.initialModalItem = void 0;
const fp = require("fp-ts");
const monocle = require("monocle-ts");
const _ActionTypes = require("./actionTypes");
const errorHandling_1 = require("../../utils/errorHandling");
const statusLens = monocle.Lens.fromProp('status');
const boardsLens = monocle.Lens.fromProp('boards');
const createIsOpenLens = monocle.Lens.fromProp('createIsOpen');
const createStatusLens = monocle.Lens.fromProp('createStatus');
const editIsOpenLens = monocle.Lens.fromProp('editIsOpen');
const editStatusLens = monocle.Lens.fromProp('editStatus');
const removeStatusLens = monocle.Lens.fromProp('removeStatus');
const modalItemLens = monocle.Lens.fromProp('modalItem');
const modalValidationLens = monocle.Lens.fromProp('modalValidation');
const reorder = (x) => x.map((x, i) => (Object.assign(Object.assign({}, x), { order: i })));
const getNewElement = (type, order = 1) => {
    switch (type) {
        case 'LINK': {
            return {
                type: 'LINK',
                id: null,
                description: '',
                url: '',
                displayName: null,
                order
            };
        }
        case 'MARKDOWN':
        case 'TEXT': {
            return {
                type,
                id: null,
                description: null,
                value: '',
                order
            };
        }
        case 'KEY-VALUE-COLUMN':
        case 'KEY-VALUE-ROW': {
            return {
                type,
                id: null,
                key: '',
                value: '',
                order
            };
        }
        default:
            return null;
    }
};
exports.initialModalItem = {
    id: null,
    name: '',
    position: 4,
    elements: []
};
function default_1(namespace) {
    const ActionTypes = _ActionTypes.default(namespace);
    const initialState = {
        status: 'STANDBY',
        boards: [],
        createIsOpen: false,
        createStatus: 'STANDBY',
        editIsOpen: false,
        editStatus: 'STANDBY',
        removeStatus: 'STANDBY',
        modalItem: Object.assign({}, exports.initialModalItem),
        modalValidation: null
    };
    const methods = {};
    // Fetch
    methods[ActionTypes.FETCH_REQUEST] = (state, action) => statusLens.set('FETCHING')(state);
    methods[ActionTypes.FETCH_SUCCESS] = (state, action) => fp.function.flow(boardsLens.modify(() => {
        const data = action.payload;
        for (const x of data) {
            x.elements = x.elements.sort((a, b) => a.order - b.order);
        }
        return data;
    }), statusLens.set('FETCHED'))(state);
    methods[ActionTypes.FETCH_FAILED] = (state, action) => statusLens.set('FAILED')(state);
    // Create
    methods[ActionTypes.CREATE_REQUEST] = (state, action) => fp.function.flow(modalValidationLens.set(null), createStatusLens.set('FETCHING'))(state);
    methods[ActionTypes.CREATE_SUCCESS] = (state, action) => fp.function.flow(createIsOpenLens.set(false), createStatusLens.set('FETCHED'))(state);
    methods[ActionTypes.CREATE_FAILED] = (state, action) => createStatusLens.set('FAILED')(state);
    methods[ActionTypes.CREATE_VALIDATION_FAILURE] = (state, action) => fp.function.flow(modalValidationLens.set((0, errorHandling_1.mapValidationErrors)(action.payload)), createStatusLens.set('FAILED'))(state);
    // Edit
    methods[ActionTypes.EDIT_REQUEST] = (state, action) => fp.function.flow(modalValidationLens.set(null), editStatusLens.set('FETCHING'))(state);
    methods[ActionTypes.EDIT_SUCCESS] = (state, action) => fp.function.flow(editIsOpenLens.set(false), editStatusLens.set('FETCHED'))(state);
    methods[ActionTypes.EDIT_FAILED] = (state, action) => editStatusLens.set('FAILED')(state);
    methods[ActionTypes.EDIT_VALIDATION_FAILURE] = (state, action) => fp.function.flow(modalValidationLens.set((0, errorHandling_1.mapValidationErrors)(action.payload)), editStatusLens.set('FAILED'))(state);
    // Remove
    methods[ActionTypes.REMOVE_REQUEST] = (state, action) => removeStatusLens.set('FETCHING')(state);
    methods[ActionTypes.REMOVE_SUCCESS] = (state, action) => removeStatusLens.set('FETCHED')(state);
    methods[ActionTypes.REMOVE_FAILED] = (state, action) => removeStatusLens.set('FAILED')(state);
    methods[ActionTypes.CHANGE_MODAL_STATUS] = (state, action) => fp.function.flow(modalValidationLens.set(null), (x) => {
        if (action.payload.modal === 'edit') {
            return fp.function.flow(editStatusLens.set('STANDBY'), editIsOpenLens.set(action.payload.status))(x);
        }
        return fp.function.flow(createStatusLens.set('STANDBY'), createIsOpenLens.set(action.payload.status))(x);
    }, (x) => {
        if (action.payload.item !== null) {
            return modalItemLens.set(JSON.parse(JSON.stringify(action.payload.item)))(x);
        }
        return x;
    })(state);
    methods[ActionTypes.UPDATE_MODAL_ITEM] = (state, action) => modalItemLens.modify((prev) => {
        const x = Object.assign({}, prev);
        if (x) {
            return Object.assign(Object.assign({}, x), { [action.payload.key]: action.payload.value });
        }
        return x;
    })(state);
    methods[ActionTypes.ADD_ELEMENT] = (state, action) => modalItemLens.modify((prev) => {
        const elem = getNewElement(action.payload);
        const x = Object.assign({}, prev);
        if (elem) {
            x.elements = reorder([...x.elements, elem]);
        }
        return x;
    })(state);
    methods[ActionTypes.UPDATE_ELEMENT] = (state, action) => modalItemLens.modify((prev) => {
        const x = Object.assign({}, prev);
        return Object.assign(Object.assign({}, x), { elements: x.elements.map((item, i) => {
                if (i === action.payload.position) {
                    return Object.assign(Object.assign({}, item), { [action.payload.key]: action.payload.value });
                }
                return item;
            }) });
    })(state);
    methods[ActionTypes.REMOVE_ELEMENT] = (state, action) => modalItemLens.modify((prev) => {
        const x = Object.assign({}, prev);
        x.elements = reorder(x.elements.filter((x, i) => i !== action.payload));
        return x;
    })(state);
    methods[ActionTypes.CLEAR] = (state, action) => (Object.assign({}, initialState));
    const reducer = (state = initialState, action) => {
        if (methods[action.type]) {
            return methods[action.type](state, action);
        }
        return state;
    };
    return { reducer, initialState, methods, ActionTypes };
}
exports.default = default_1;
//# sourceMappingURL=reducers.js.map